import { Link } from "react-router-dom";
import { Code2, Globe, ShoppingCart, BarChart3, ArrowRight, CheckCircle2 } from "lucide-react";
import Spotlight from "../components/Spotlight";
import SectionTransition from "../components/SectionTransition";

const services = [
  {
    icon: Code2,
    title: "Software Development",
    desc: "We build robust, scalable software solutions that power your business operations. From desktop applications to enterprise systems, our team delivers code that performs.",
    features: [
      "Custom Enterprise Applications",
      "API Development & Integration",
      "Legacy System Modernization",
      "Cloud-Native Architecture",
      "Quality Assurance & Testing",
      "Continuous Deployment Pipelines",
    ],
    image: "https://images.unsplash.com/photo-1580894894513-541e068a3e2b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    icon: Globe,
    title: "Web Development",
    desc: "High-performance, responsive web applications built with modern frameworks. We create digital experiences that engage users and drive conversions.",
    features: [
      "React & Next.js Applications",
      "Progressive Web Apps (PWA)",
      "Responsive UI/UX Design",
      "Performance Optimization",
      "SEO-Friendly Architecture",
      "Real-Time Web Applications",
    ],
    image: "https://images.unsplash.com/photo-1765561667528-28e39c6174dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    icon: ShoppingCart,
    title: "E-Commerce Development",
    desc: "End-to-end e-commerce solutions that transform online shopping into seamless, revenue-generating experiences. We build stores that sell.",
    features: [
      "Custom Storefront Development",
      "Payment Gateway Integration",
      "Inventory Management Systems",
      "Multi-Vendor Marketplace",
      "Shopping Cart Optimization",
      "Analytics & Reporting Dashboards",
    ],
    image: "https://images.unsplash.com/photo-1686061594225-3e92c0cd51b0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    icon: BarChart3,
    title: "Digital Marketing",
    desc: "Data-driven marketing strategies that amplify your brand presence and drive measurable results. We bridge the gap between technology and growth.",
    features: [
      "SEO & Content Strategy",
      "Social Media Management",
      "Pay-Per-Click Advertising",
      "Email Marketing Automation",
      "Brand Identity Development",
      "Analytics & Performance Tracking",
    ],
    image: "https://images.unsplash.com/photo-1686061593213-98dad7c599b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <Spotlight className="bg-neutral-950 grid-pattern pt-32 pb-20 md:pt-44 md:pb-28">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-wider uppercase text-blue-400 mb-4 animate-fade-in-up">Our Services</p>
            <h1
              data-testid="services-heading"
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6 animate-fade-in-up animation-delay-200"
            >
              What We Do
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed animate-fade-in-up animation-delay-400">
              We deliver comprehensive technology solutions that empower businesses to 
              innovate, scale, and lead in their markets.
            </p>
          </div>
        </div>
      </Spotlight>

      {/* Services - Zig Zag */}
      <section className="bg-white">
        {services.map((service, i) => (
          <div
            key={service.title}
            data-testid={`service-section-${i}`}
            className={`py-24 md:py-32 ${i % 2 !== 0 ? "bg-slate-50" : "bg-white"}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${i % 2 !== 0 ? "lg:flex-row-reverse" : ""}`}>
                {/* Text */}
                <SectionTransition className={i % 2 !== 0 ? "lg:order-2" : ""}>
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-8">
                    <service.icon size={28} className="text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                    {service.title}
                  </h2>
                  <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8">{service.desc}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-slate-700 text-sm">
                        <CheckCircle2 size={18} className="text-blue-500 shrink-0" strokeWidth={1.5} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm hover:text-blue-800 transition-colors"
                  >
                    Discuss Your Project <ArrowRight size={16} />
                  </Link>
                </SectionTransition>

                {/* Image */}
                <SectionTransition delay={0.2} className={i % 2 !== 0 ? "lg:order-1" : ""}>
                  <div className="rounded-2xl overflow-hidden border border-slate-200">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[300px] md:h-[400px] object-cover"
                      loading="lazy"
                    />
                  </div>
                </SectionTransition>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-neutral-950 py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <SectionTransition>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              Have a Project in Mind?
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-xl mx-auto mb-10">
              Tell us about your requirements and we will craft the perfect solution for your business.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-slate-200 transition-all duration-200 active:scale-95"
            >
              Start a Conversation <ArrowRight size={16} />
            </Link>
          </SectionTransition>
        </div>
      </section>
    </div>
  );
}
